import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Trans, useLingui } from '@lingui/react';
import { utils } from '@orionprotocol/sdk';
import { Button, Dialog, Icon, WithTooltip } from '@orionprotocol/ui';
import { useShouldSwitchNetworkManually } from '@orionprotocol/utils';

import { useChangeWalletNetwork, useAppNetwork } from '../../../shared/hooks';
import { useChangeWalletNetworkAnalyticEvents } from '../../../shared/analytics';
import {
  useChains,
  useWalletChainId,
  useWalletConnectWalletName,
  useWalletType,
} from '../../../shared/api';

import styles from './NetworksDontMatchWarning.module.scss';

export interface NetworksDontMatchWarningProps {
  onNetworkChanging?: () => void;
}

export function NetworksDontMatchWarning({
  onNetworkChanging,
}: NetworksDontMatchWarningProps): JSX.Element {
  const { i18n } = useLingui();

  const walletChainId = useWalletChainId();
  const walletChainIdHex = `${walletChainId?.toString(10) ?? ''}`;
  const appChainInfo = useAppNetwork();
  const chains = useChains();

  const walletChainInfo = utils.isValidChainId(walletChainIdHex)
    ? chains[walletChainIdHex]
    : undefined;
  const walletType = useWalletType();
  const walletConnectWalletName = useWalletConnectWalletName();
  const walletIsMetamask = walletType === 'MetaMask';
  const changeWalletNetworkEvents = useChangeWalletNetworkAnalyticEvents();
  const changeWalletNetwork = useChangeWalletNetwork(changeWalletNetworkEvents);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const shouldSwitchNetworkManually = useShouldSwitchNetworkManually(
    appChainInfo.code,
    walletConnectWalletName,
    walletType,
  );

  const onSwitchClick = useCallback(async (): Promise<void> => {
    onNetworkChanging?.();
    await changeWalletNetwork(appChainInfo.chainId);
  }, [appChainInfo, onNetworkChanging, changeWalletNetwork]);

  const overlay = useMemo(
    () => (
      <div className={styles.tooltip}>
        <Trans
          id="app_network_doesnt_match"
          message="App network {appNetwork} doesn't match the network selected in wallet ({walletNetwork})"
          values={{
            appNetwork: appChainInfo.label,
            walletNetwork: walletChainInfo
              ? walletChainInfo.label
              : walletChainId,
          }}
        />
        {shouldSwitchNetworkManually ? (
          <p className={styles.warning}>
            <Trans
              id="please_change_network_in_wallet"
              message="Please change network in wallet"
            />
          </p>
        ) : (
          <Button
            className={styles.fixNetworkButton}
            type="outlined"
            onClick={(): void => {
              onSwitchClick().catch(() =>
                console.error('Failed to change network'),
              );
            }}
            wide
            size="xs"
          >
            {walletIsMetamask ? (
              <Trans id="switch_network" message="Switch network" />
            ) : (
              <Trans id="switch_wallet" message="Switch wallet" />
            )}
          </Button>
        )}
      </div>
    ),
    [
      appChainInfo.label,
      onSwitchClick,
      shouldSwitchNetworkManually,
      walletChainId,
      walletChainInfo,
      walletIsMetamask,
    ],
  );

  return (
    <Dialog
      open={!isMobile ? false : dialogIsOpen}
      onOpenChange={setDialogIsOpen}
      trigger={
        <WithTooltip
          tooltip={overlay}
          width={250}
          mobileWidth={200}
          side="bottom"
          active={!isMobile}
        >
          <div className={styles.root}>
            <Icon icon="warning" className={styles.warningIcon} />
          </div>
        </WithTooltip>
      }
      className={styles.dialogRoot}
      title={i18n._('Select network')}
    >
      <div className={styles.dialogContent}>{overlay}</div>
    </Dialog>
  );
}
